import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import { useToast } from '../components/Toast';
import { sendActivityNotification } from '../components/TelegramNotify';
import { formatUSDT, parseUSDT } from '../utils/helpers';
import { PRICE_TIERS } from '../config/constants';
import NFTCard from '../components/NFTCard';
import Modal from '../components/Modal';
import Loader from '../components/Loader';
import './Market.css';

const NFTS_PER_PAGE = 25;
const FETCH_LIMIT = 100; // Limit for fetching NFTs per price tier

const Market = () => {
  const {
    account,
    planContract,
    engineContract,
    usdtContract,
    usdtBalance,
    userDetails,
    planConfig,
    refreshUserData
  } = useWeb3();
  const toast = useToast();
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceFilter, setPriceFilter] = useState('all');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState('50');
  const [currentPage, setCurrentPage] = useState(1);

  const isAutoUser = userDetails?.isAutoUser || false;
  const isNormalUser = userDetails?.isNormalUser || false;

  const [purchaseMode, setPurchaseMode] = useState('auto');

  // Investment caps (BigNumber math — no precision loss)
  const zero = ethers.BigNumber.from(0);
  const rawMaxAuto = planConfig?.maxAutoInvestment || zero;
  const rawMaxNormal = planConfig?.maxNormalInvestment || zero;
  const rawAutoInv = userDetails?.autoInvested || zero;
  const rawNormalInv = userDetails?.normalInvested || zero;
  const rawAutoRemaining = rawMaxAuto.gt(rawAutoInv) ? rawMaxAuto.sub(rawAutoInv) : zero;
  const rawNormalRemaining = rawMaxNormal.gt(rawNormalInv) ? rawMaxNormal.sub(rawNormalInv) : zero;

  const autoRemaining = parseFloat(ethers.utils.formatUnits(rawAutoRemaining, 18));
  const normalRemaining = parseFloat(ethers.utils.formatUnits(rawNormalRemaining, 18));
  const normalModeEnabled = planConfig?.normalModeEnabled !== false;

  useEffect(() => {
    if (isAutoUser) setPurchaseMode('auto');
    else if (isNormalUser) setPurchaseMode('normal');
  }, [isAutoUser, isNormalUser]);

  // Position dropdown when opened
  useEffect(() => {
    if (showDropdown && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: `${rect.bottom + 8}px`,
        left: `${rect.left}px`,
        minWidth: `${rect.width}px`
      });
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchNFTs();
    const interval = setInterval(fetchNFTs, 15000);
    return () => clearInterval(interval);
  }, [engineContract, priceFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [priceFilter]);

  const fetchNFTs = async () => {
    if (!engineContract) {
      setLoading(false);
      return;
    }

    try {
      const priceData = await engineContract.getAllActiveNFTsByPrice();
      const allNFTs = [];

      for (let i = 0; i < priceData[0].length; i++) {
        const price = priceData[0][i];
        const count = priceData[1][i].toNumber();

        if (count > 0) {
          const result = await engineContract.getNFTsByPrice(price, 0, FETCH_LIMIT);
          const nftIds = result.nftIds;

          for (let j = 0; j < nftIds.length; j++) {
            const details = await engineContract.getNFTDetails(nftIds[j]);
            if (details.isActive) {
              allNFTs.push({
                nftId: nftIds[j].toString(),
                currentPrice: details.currentPrice,
                purchasePrice: details.purchasePrice,
                owner: details.nftOwner,
                lastPurchaseTime: details.lastPurchaseTime,
                isActive: details.isActive
              });
            }
          }
        }
      }

      let filteredNFTs = allNFTs;
      if (priceFilter !== 'all') {
        const filterPrice = parseFloat(priceFilter);
        filteredNFTs = allNFTs.filter(nft => 
          parseFloat(formatUSDT(nft.currentPrice)) === filterPrice
        );
      }

      setNfts(filteredNFTs);
    } catch (error) {
      console.error('Error fetching NFTs:', error);
      toast.error('Failed to load NFTs');
    } finally {
      setLoading(false);
    }
  };

  // Clicking any NFT opens the Buy More modal
  const handleNFTClick = () => {
    setShowPurchaseModal(true);
  };

  const handlePurchase = async () => {
    const amt = parseFloat(purchaseAmount);
    if (amt < 20) {
      toast.error('Minimum purchase is $20');
      return;
    }

    if (parseFloat(usdtBalance) < amt) {
      toast.error('Insufficient USDT balance');
      return;
    }

    const isAutoPurchase = purchaseMode === 'auto';
    const rawAmount = parseUSDT(purchaseAmount);
    if (isAutoPurchase && rawAmount.gt(rawAutoRemaining)) {
      toast.error(`Auto cap exceeded. Remaining: $${autoRemaining.toFixed(2)}`);
      return;
    }
    if (!isAutoPurchase && rawAmount.gt(rawNormalRemaining)) {
      toast.error(`Normal cap exceeded. Remaining: $${normalRemaining.toFixed(2)}`);
      return;
    }
    if (!isAutoPurchase && !normalModeEnabled) {
      toast.error('Normal mode is currently disabled');
      return;
    }

    setIsPurchasing(true);
    try {
      const amount = parseUSDT(purchaseAmount);
      const allowance = await usdtContract.allowance(account, planContract.address);

      if (allowance.lt(amount)) {
        toast.info('Approving USDT...');
        const approveTx = await usdtContract.approve(planContract.address, ethers.constants.MaxUint256);
        await approveTx.wait();
        toast.success('USDT approved!');
      }

      toast.info('Purchasing NFTs...');
      const tx = await planContract.purchaseNFTs(
        amount,
        ethers.constants.AddressZero,
        isAutoPurchase,
        !isAutoPurchase
      );
      await tx.wait();

      toast.success('Purchase successful!');
      sendActivityNotification('PUR', purchaseAmount);
      setShowPurchaseModal(false);
      await refreshUserData();
      await fetchNFTs();
    } catch (error) {
      toast.error(error.reason || 'Transaction failed');
    } finally {
      setIsPurchasing(false);
    }
  };

  const getFilterLabel = () => {
    if (priceFilter === 'all') return 'All Prices';
    return `$${priceFilter}`;
  };

  // Pagination
  const totalPages = Math.ceil(nfts.length / NFTS_PER_PAGE);
  const paginatedNFTs = nfts.slice(
    (currentPage - 1) * NFTS_PER_PAGE,
    currentPage * NFTS_PER_PAGE
  );

  if (loading) {
    return <Loader text="Loading Market" />;
  }

  return (
    <div className="market-page">
      {/* Header */}
      <div className="market-header animate-fade-up">
        <div className="header-info">
          <h1 className="page-title">
            <span className="title-icon pulse-icon">🛒</span>
            NFT Market
          </h1>
          <p className="nft-count font-numbers">{nfts.length} NFTs available</p>
        </div>

        {/* Price Filter Dropdown */}
        <div className="filter-dropdown">
          <button 
            ref={triggerRef}
            className="dropdown-trigger"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <span className="filter-icon">◈</span>
            <span className="filter-label">{getFilterLabel()}</span>
            <span className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}>▼</span>
          </button>
          
          {showDropdown && (
            <div className="dropdown-menu" ref={dropdownRef} style={dropdownStyle}>
              <button 
                className={`dropdown-item ${priceFilter === 'all' ? 'active' : ''}`}
                onClick={() => { setPriceFilter('all'); setShowDropdown(false); }}
              >
                All Prices
              </button>
              {PRICE_TIERS.map(tier => (
                <button 
                  key={`tier-${tier.price}`}
                  className={`dropdown-item ${priceFilter === tier.price.toString() ? 'active' : ''}`}
                  onClick={() => { setPriceFilter(tier.price.toString()); setShowDropdown(false); }}
                >
                  {tier.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Buy Button */}
      <button className="market-buy-btn animate-fade-up" onClick={() => setShowPurchaseModal(true)}>
        <span className="btn-icon bounce-icon">🛒</span>
        <span className="btn-text">Buy NFTs</span>
      </button>

      {/* Info Banner */}
      <div className="market-info animate-fade-up">
        <span>💡</span>
        <span>Click "Buy NFTs" to purchase. NFTs are automatically assigned from the available.</span>
      </div>

      {/* NFT Grid */}
      <div className="nft-grid">
        {paginatedNFTs.length === 0 ? (
          <div className="empty-state animate-fade-up">
            <div className="empty-icon">🔍</div>
            <h3>No NFTs Found</h3>
            <p>No NFTs available at this price tier</p>
          </div>
        ) : (
          paginatedNFTs.map((nft, index) => (
            <div 
              key={`nft-${nft.nftId}`} 
              className="animate-fade-up" 
              style={{ animationDelay: `${index * 0.03}s` }}
            >
              <NFTCard nft={nft} onSelect={handleNFTClick} />
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination animate-fade-up">
          <button 
            className="page-btn"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            ««
          </button>
          <button 
            className="page-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          
          <div className="page-numbers">
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={`page-${pageNum}`}
                  className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button 
            className="page-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
          <button 
            className="page-btn"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            »»
          </button>
        </div>
      )}

      {/* Purchase Modal */}
      <Modal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        title="Buy NFTs"
        size="small"
      >
        <div className="purchase-modal">
          <div className="purchase-balance">
            <span>Your Balance</span>
            <span className="font-numbers">{parseFloat(usdtBalance).toFixed(2)} USDT</span>
          </div>

          {/* Mode toggle */}
          <div className="mode-toggle" style={{ display: 'flex', gap: '8px', margin: '12px 0' }}>
            <button
              type="button"
              className={`amount-btn ${purchaseMode === 'auto' ? 'active' : ''}`}
              style={{ flex: 1 }}
              onClick={() => setPurchaseMode('auto')}
            >
              🚀 Auto<br/>
              <span style={{ fontSize: '0.7em', opacity: 0.75 }}>
                Remaining ${autoRemaining.toFixed(2)}
              </span>
            </button>
            <button
              type="button"
              className={`amount-btn ${purchaseMode === 'normal' ? 'active' : ''}`}
              style={{ flex: 1, opacity: normalModeEnabled ? 1 : 0.4 }}
              disabled={!normalModeEnabled}
              onClick={() => normalModeEnabled && setPurchaseMode('normal')}
            >
              💰 Normal<br/>
              <span style={{ fontSize: '0.7em', opacity: 0.75 }}>
                {normalModeEnabled ? `Remaining $${normalRemaining.toFixed(2)}` : 'Disabled'}
              </span>
            </button>
          </div>

          <div className="purchase-amounts">
            {[20, 50, 100, 200, 500, 1000].map((amt) => (
              <button
                key={`amt-${amt}`}
                className={`amount-btn ${purchaseAmount === amt.toString() ? 'active' : ''}`}
                onClick={() => setPurchaseAmount(amt.toString())}
              >
                <span className="font-numbers">${amt}</span>
              </button>
            ))}
          </div>

          <div className="purchase-input">
            <input
              type="number"
              className="cyber-input"
              value={purchaseAmount}
              onChange={(e) => setPurchaseAmount(e.target.value)}
              min="20"
            />
            <span className="input-suffix">USDT</span>
          </div>

          <div className="purchase-note">
            <p>💡 NFTs will be purchased automatically from the available in the market.</p>
            <p>🔄 You cannot select specific NFTs - the system assigns NFTs optimally for fair distribution.</p>
          </div>

          <button 
            className="btn btn-primary btn-purchase"
            onClick={handlePurchase}
            disabled={isPurchasing}
          >
            {isPurchasing ? 'Processing...' : 'Purchase Now'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Market;
